import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../App';
import { checkRankUp } from '../utils/points';
import './RetroEscapeRoom.css';

const RetroEscapeRoom = ({ user }) => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  
  // Game State
  const [caseData, setCaseData] = useState(null);
  const [caseDb, setCaseDb] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [endMessage, setEndMessage] = useState('');
  
  // Gameplay State
  const [step, setStep] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [inAltPath, setInAltPath] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [feedbackType, setFeedbackType] = useState('');
  const [showNextButton, setShowNextButton] = useState(false);
  const [nextButtonText, setNextButtonText] = useState('');
  const [nextButtonAction, setNextButtonAction] = useState(null);
  const [optionsDisabled, setOptionsDisabled] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  
  // Visual State
  const [isCritical, setIsCritical] = useState(false);
  const [isAsystole, setIsAsystole] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [heartRate, setHeartRate] = useState(72);
  const [bloodPressure, setBloodPressure] = useState('120/80');
  
  // Audio Context
  const audioContextRef = useRef(null);
  const masterGainRef = useRef(null);
  const flatlineOscRef = useRef(null);
  const audioEnabledRef = useRef(false);
  
  // ECG Canvas
  const canvasRef = useRef(null);
  const ecgStateRef = useRef({
    running: false,
    x: 0,
    yBase: 60,
    lastBeat: 0,
    beatInterval: 850,
    animationId: null
  });

  // Transform CSV data to caseDb format
  const transformToCaseDb = useCallback((steps) => {
    return steps.map((step, index) => ({
      id: index + 1,
      mainQ: step.Pregunta_Principal || '',
      options: [
        { id: 'A', text: step.A_Principal || '', correct: step.RespuestaCorrecta_Principal === 'A' },
        { id: 'B', text: step.B_Principal || '', correct: step.RespuestaCorrecta_Principal === 'B' },
        { id: 'C', text: step.C_Principal || '', correct: step.RespuestaCorrecta_Principal === 'C' },
        { id: 'D', text: step.D_Principal || '', correct: step.RespuestaCorrecta_Principal === 'D' }
      ].filter(opt => opt.text),
      msgOk: step.MensajeCorrecto_Principal || 'Correcto.',
      msgFail: step.MensajeIncorrecto_Principal || 'Incorrecto.',
      altQ: step.Pregunta_Alternativa || '',
      altOptions: [
        { id: 'A', text: step.A_Alternativa || '', correct: step.RespuestaCorrecta_Alternativa === 'A' },
        { id: 'B', text: step.B_Alternativa || '', correct: step.RespuestaCorrecta_Alternativa === 'B' },
        { id: 'C', text: step.C_Alternativa || '', correct: step.RespuestaCorrecta_Alternativa === 'C' },
        { id: 'D', text: step.D_Alternativa || '', correct: step.RespuestaCorrecta_Alternativa === 'D' }
      ].filter(opt => opt.text),
      msgAltOk: step.MensajeCorrecto_Alternativa || 'Recuperado.',
      msgDeath: step.MensajeMuerte || 'El paciente falleció.'
    }));
  }, []);

  // Fetch case data
  useEffect(() => {
    const fetchCase = async () => {
      try {
        const response = await api.get(`/cases/${caseId}`);
        const data = response.data;
        setCaseData(data);
        
        if (data.steps && data.steps.length > 0) {
          const transformed = transformToCaseDb(data.steps);
          setCaseDb(transformed);
        }
      } catch (error) {
        console.error('Error fetching case:', error);
        toast.error('Error al cargar el caso clínico');
        navigate('/escape-room');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCase();
  }, [caseId, navigate, transformToCaseDb]);

  // Cleanup audio when component unmounts
  useEffect(() => {
    return () => {
      // Stop flatline sound
      if (flatlineOscRef.current) {
        try {
          flatlineOscRef.current.stop();
        } catch (e) {}
        flatlineOscRef.current = null;
      }
      
      // Close audio context
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch (e) {}
        audioContextRef.current = null;
      }
      
      // Stop ECG animation
      if (ecgStateRef.current.animationId) {
        cancelAnimationFrame(ecgStateRef.current.animationId);
      }
      ecgStateRef.current.running = false;
      
      audioEnabledRef.current = false;
    };
  }, []);

  // Initialize Audio System
  const initAudio = useCallback(() => {
    if (audioContextRef.current) return;
    
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    audioContextRef.current = new AudioContext();
    
    masterGainRef.current = audioContextRef.current.createGain();
    masterGainRef.current.connect(audioContextRef.current.destination);
    masterGainRef.current.gain.value = 1;
    
    audioEnabledRef.current = true;
  }, []);

  // Audio beep function
  const playBeep = useCallback(() => {
    if (!audioEnabledRef.current || !audioContextRef.current || flatlineOscRef.current) return;
    
    const ctx = audioContextRef.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(masterGainRef.current);
    
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(900, ctx.currentTime);
    
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    
    osc.start();
    osc.stop(ctx.currentTime + 0.15);
  }, []);

  // Start flatline sound
  const startFlatline = useCallback(() => {
    if (!audioEnabledRef.current || !audioContextRef.current || flatlineOscRef.current) return;
    
    const ctx = audioContextRef.current;
    flatlineOscRef.current = ctx.createOscillator();
    const gain = ctx.createGain();
    
    flatlineOscRef.current.connect(gain);
    gain.connect(masterGainRef.current);
    
    flatlineOscRef.current.type = 'sine';
    flatlineOscRef.current.frequency.setValueAtTime(800, ctx.currentTime);
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    
    flatlineOscRef.current.start();
  }, []);

  // Stop flatline sound
  const stopFlatline = useCallback(() => {
    if (flatlineOscRef.current) {
      try {
        flatlineOscRef.current.stop();
      } catch (e) {}
      flatlineOscRef.current = null;
    }
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (masterGainRef.current && audioContextRef.current) {
      if (isMuted) {
        masterGainRef.current.gain.setValueAtTime(1, audioContextRef.current.currentTime);
      } else {
        masterGainRef.current.gain.setValueAtTime(0, audioContextRef.current.currentTime);
      }
    }
    setIsMuted(!isMuted);
  }, [isMuted]);

  // ECG Animation
  useEffect(() => {
    if (!gameStarted || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const state = ecgStateRef.current;
    
    // Setup canvas
    canvas.width = canvas.parentElement.offsetWidth;
    canvas.height = canvas.parentElement.offsetHeight;
    
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.shadowBlur = 5;
    
    state.running = true;
    state.x = 0;
    state.yBase = canvas.height / 2;
    
    const drawQRS = (sx) => {
      const y = state.yBase;
      
      ctx.beginPath();
      ctx.moveTo(sx, y);
      ctx.lineTo(sx + 5, y - 5);
      ctx.lineTo(sx + 10, y);
      ctx.lineTo(sx + 12, y + 5);
      ctx.lineTo(sx + 18, y - 40);
      ctx.lineTo(sx + 24, y + 10);
      ctx.lineTo(sx + 28, y);
      ctx.quadraticCurveTo(sx + 35, y - 10, sx + 42, y);
      ctx.stroke();
      
      state.x += 42;
    };
    
    const animate = (timestamp) => {
      if (!state.running) return;
      
      ctx.fillStyle = 'rgba(5, 16, 5, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      if (isAsystole) {
        ctx.strokeStyle = 'red';
        ctx.shadowColor = 'red';
        ctx.beginPath();
        ctx.moveTo(state.x, state.yBase);
        state.x += 2;
        ctx.lineTo(state.x, state.yBase);
        ctx.stroke();
      } else {
        ctx.strokeStyle = isCritical ? '#ff6666' : '#33ff33';
        ctx.shadowColor = isCritical ? '#ff6666' : '#33ff33';
        
        if (timestamp - state.lastBeat > state.beatInterval) {
          drawQRS(state.x);
          state.lastBeat = timestamp;
          playBeep();
          state.beatInterval = (isCritical ? 400 : 850) + (Math.random() * 50 - 25);
        } else {
          ctx.beginPath();
          ctx.moveTo(state.x, state.yBase);
          state.x += 2;
          ctx.lineTo(state.x, state.yBase);
          ctx.stroke();
        }
      }
      
      if (state.x > canvas.width) {
        state.x = 0;
      }
      
      state.animationId = requestAnimationFrame(animate);
    };
    
    state.animationId = requestAnimationFrame(animate);
    
    return () => {
      state.running = false;
      if (state.animationId) {
        cancelAnimationFrame(state.animationId);
      }
    };
  }, [gameStarted, isCritical, isAsystole, playBeep]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current && ecgStateRef.current.running) {
        canvasRef.current.width = canvasRef.current.parentElement.offsetWidth;
        ecgStateRef.current.yBase = canvasRef.current.height / 2;
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update vitals based on critical state
  useEffect(() => {
    if (isCritical && !isAsystole) {
      setHeartRate(135 + Math.floor(Math.random() * 20));
      setBloodPressure(`${80 + Math.floor(Math.random() * 10)}/${50 + Math.floor(Math.random() * 10)}`);
      ecgStateRef.current.beatInterval = 400;
    } else if (!isAsystole) {
      setHeartRate(70 + Math.floor(Math.random() * 5));
      setBloodPressure('120/80');
      ecgStateRef.current.beatInterval = 850;
    }
  }, [isCritical, isAsystole]);

  // Start game
  const startGame = useCallback(() => {
    initAudio();
    setGameStarted(true);
    setStep(0);
    setMistakes(0);
    setInAltPath(false);
    setIsCritical(false);
    setIsAsystole(false);
    setFeedback(null);
    setShowNextButton(false);
    setOptionsDisabled(false);
    setSelectedOption(null);
  }, [initAudio]);

  // Trigger asystole
  const triggerAsystole = useCallback(() => {
    setIsAsystole(true);
    startFlatline();
    setHeartRate(0);
    setBloodPressure('---');
  }, [startFlatline]);

  // Game over
  const gameOver = useCallback((win, reason = '') => {
    setTimeout(() => {
      if (win) {
        ecgStateRef.current.running = false;
        stopFlatline();
      }
      setGameEnded(true);
      setGameWon(win);
      
      // Usar mensajes del CSV (global_messages)
      if (win) {
        setEndMessage(caseData?.global_messages?.MensajeCuracion || 'Has completado el caso exitosamente.');
      } else {
        setEndMessage(caseData?.global_messages?.MensajeMuerte || reason || 'El paciente ha fallecido.');
      }
      
      // Record progress if won
      if (win) {
        api.post('/progress/escape-room-complete', { case_id: caseId })
          .then(res => checkRankUp(res.data))
          .catch(err => console.error('Error saving progress:', err));
      }
    }, 3000);
  }, [caseId, caseData, stopFlatline]);

  // Handle answer selection
  const handleAnswer = useCallback((option) => {
    if (optionsDisabled || caseDb.length === 0) return;
    
    setSelectedOption(option.id);
    const currentStep = caseDb[step];
    setOptionsDisabled(true);
    
    if (!inAltPath) {
      // Main path
      if (option.correct) {
        setFeedback(currentStep.msgOk);
        setFeedbackType('correct');
        setNextButtonText('SIGUIENTE PASO >>');
        setShowNextButton(true);
        setNextButtonAction(() => () => {
          if (step + 1 >= caseDb.length) {
            gameOver(true);
          } else {
            setStep(prev => prev + 1);
            setFeedback(null);
            setShowNextButton(false);
            setOptionsDisabled(false);
            setSelectedOption(null);
            setIsCritical(false);
          }
        });
      } else {
        const newMistakes = mistakes + 1;
        setMistakes(newMistakes);
        
        if (newMistakes > 1) {
          triggerAsystole();
          gameOver(false, 'Has cometido 2 errores. ASISTOLIA IRREVERSIBLE. Paciente fallecido.');
        } else {
          setIsCritical(true);
          setFeedback(`${currentStep.msgFail} ALERTA: CHOQUE HEMODINÁMICO. Tienes 1 oportunidad para estabilizar.`);
          setFeedbackType('warning');
          setNextButtonText('MANIOBRAS DE RESCATE >>');
          setShowNextButton(true);
          setNextButtonAction(() => () => {
            setInAltPath(true);
            setFeedback(null);
            setShowNextButton(false);
            setOptionsDisabled(false);
            setSelectedOption(null);
          });
        }
      }
    } else {
      // Alternative path
      if (option.correct) {
        setFeedback(currentStep.msgAltOk);
        setFeedbackType('correct');
        setNextButtonText('ESTABILIZADO - CONTINUAR >>');
        setShowNextButton(true);
        setNextButtonAction(() => () => {
          setInAltPath(false);
          setIsCritical(false);
          if (step + 1 >= caseDb.length) {
            gameOver(true);
          } else {
            setStep(prev => prev + 1);
            setFeedback(null);
            setShowNextButton(false);
            setOptionsDisabled(false);
            setSelectedOption(null);
          }
        });
      } else {
        const newMistakes = mistakes + 1;
        setMistakes(newMistakes);
        triggerAsystole();
        gameOver(false, currentStep.msgDeath + ' (Paro Cardíaco)');
      }
    }
  }, [caseDb, step, inAltPath, mistakes, optionsDisabled, gameOver, triggerAsystole]);

  // Restart game
  const restartGame = useCallback(() => {
    stopFlatline();
    setGameStarted(false);
    setGameEnded(false);
    setGameWon(false);
    setEndMessage('');
    setStep(0);
    setMistakes(0);
    setInAltPath(false);
    setIsCritical(false);
    setIsAsystole(false);
    setFeedback(null);
    setShowNextButton(false);
    setOptionsDisabled(false);
    setHeartRate(72);
    setBloodPressure('120/80');
  }, [stopFlatline]);

  if (loading) {
    return (
      <div className="retro-loading">
        <div className="retro-spinner"></div>
        <p>Cargando caso clínico...</p>
      </div>
    );
  }

  if (!caseData || caseDb.length === 0) {
    return (
      <div className="retro-loading">
        <p>Error al cargar el caso</p>
        <button onClick={() => navigate('/escape-room')} className="btn-start">
          VOLVER
        </button>
      </div>
    );
  }

  const currentStepData = caseDb[step] || {};
  const currentQuestion = inAltPath ? currentStepData.altQ : currentStepData.mainQ;
  const currentOptions = inAltPath ? currentStepData.altOptions : currentStepData.options;

  return (
    <div className="retro-body">
      <div 
        id="monitor-case" 
        className={isCritical ? 'critical-state' : ''}
        data-testid="retro-monitor"
      >
        {/* Header */}
        <header id="main-header">
          <div className="header-title">{caseData?.theme || 'CASO CLÍNICO'}</div>
          
          <div className="controls-inline">
            <div 
              className="icon-btn" 
              id="audio-btn" 
              onClick={toggleMute} 
              title="Silenciar/Activar Audio"
              data-testid="audio-toggle-btn"
            >
              {isMuted ? '🔇' : '🔊'}
            </div>
            <div 
              className="icon-btn" 
              onClick={() => navigate('/escape-room')} 
              title="Salir"
              data-testid="exit-btn"
            >
              ✕
            </div>
          </div>

          <div className="vitals-container">
            <div className="vital">
              <span className="vital-label">ERRORES</span>
              <span 
                className={`vital-value ${mistakes >= 2 ? 'critical-text' : ''}`} 
                id="mistake-counter"
                data-testid="mistake-counter"
              >
                {mistakes}/2
              </span>
            </div>
            <div className={`vital ${isCritical ? 'vital-critical' : ''}`} id="bp-box">
              <span className="vital-label">PA</span>
              <span 
                className={`vital-value ${isCritical || isAsystole ? 'critical-text' : ''}`} 
                id="bp-display"
                data-testid="bp-display"
              >
                {bloodPressure}
              </span>
            </div>
            <div className={`vital ${isCritical ? 'vital-critical' : ''}`} id="hr-box">
              <span className="vital-label">FC</span>
              <span 
                className={`vital-value ${isCritical || isAsystole ? 'critical-text' : ''}`} 
                id="hr-display"
                data-testid="hr-display"
              >
                {heartRate}
              </span>
            </div>
          </div>
        </header>

        {/* ECG Area */}
        <div id="ecg-area">
          <div className="grid-bg"></div>
          <canvas id="ecg-canvas" ref={canvasRef}></canvas>
        </div>

        {/* Game Interface */}
        <div id="interface">
          {gameStarted && !gameEnded && (
            <>
              <div 
                id="step-info" 
                data-testid="step-info"
              >
                PASO {step + 1} DE {caseDb.length}
              </div>
              
              <div 
                id="question-text" 
                className={`question-box ${inAltPath ? 'critical-question' : ''}`}
                data-testid="question-text"
              >
                {currentQuestion}
              </div>
              
              <div id="options-container" className="options-grid" data-testid="options-container">
                {currentOptions?.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => handleAnswer(opt)}
                    disabled={optionsDisabled}
                    className={`option-btn ${selectedOption === opt.id ? 'option-selected' : ''}`}
                    data-testid={`option-${opt.id}`}
                  >
                    [{opt.id}] {opt.text}
                  </button>
                ))}
              </div>
              
              {feedback && (
                <div 
                  id="feedback-area" 
                  className={`feedback ${feedbackType}`}
                  data-testid="feedback-area"
                >
                  {feedback}
                </div>
              )}
              
              {showNextButton && (
                <button 
                  id="next-btn" 
                  className="next-btn"
                  onClick={nextButtonAction}
                  data-testid="next-btn"
                >
                  {nextButtonText}
                </button>
              )}
            </>
          )}
        </div>

        {/* Start Screen */}
        {!gameStarted && !gameEnded && (
          <div id="start-screen" className="overlay-screen" data-testid="start-screen">
            <h1 className="start-title">{caseData?.title || 'CASO CLÍNICO'}</h1>
            <h3 className="start-subtitle">SIMULACIÓN CLÍNICA AVANZADA</h3>
            <div className="start-rules">
              <strong>REGLAS DE SUPERVIVENCIA:</strong><br /><br />
              1. <strong>Primer Error:</strong> El paciente entra en CHOQUE (Taquicardia/Hipotensión).<br />
              2. <strong>Segundo Error:</strong> El paciente entra en ASISTOLIA (Paro Cardíaco) y el caso termina.
            </div>
            <p className="start-audio-note">
              * Haga clic en "INICIAR CASO" para activar el audio.
            </p>
            <button className="btn-start" onClick={startGame} data-testid="start-btn">
              INICIAR CASO
            </button>
          </div>
        )}

        {/* End Screen */}
        {gameEnded && (
          <div id="end-screen" className="overlay-screen" data-testid="end-screen">
            {gameWon && (
              <h1 
                id="end-title" 
                className="end-title end-title-win"
              >
                CASO COMPLETADO
              </h1>
            )}
            <p id="end-msg" className="end-message">
              {endMessage}
            </p>
            <div className="end-buttons">
              <button className="btn-start" onClick={restartGame} data-testid="restart-btn">
                REPETIR SIMULACIÓN
              </button>
              <button 
                className="btn-start btn-secondary" 
                onClick={() => navigate('/escape-room')}
                data-testid="back-btn"
              >
                VOLVER AL MENÚ
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetroEscapeRoom;
